import * as React from 'react'
import { Theme, styled } from '@mui/material/styles'
import { FC, useState, useCallback, useMemo } from 'react'
import { SubMenu } from './SubMenu'

import StarIcon from '@mui/icons-material/Star'
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
// Imported from the module rather than the barrel: index.ts re-exports
// SideNav, which imports this file, so going through '.' hands back an
// uninitialised binding — and `styles` below reads these at module scope.
import { NavItem, navItemTileStyle, navItemTextStyle } from './NavItem'

const StyledMenuTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.background.paper,
    fontSize: '19px',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    margin: `0 0 0 ${theme.spacing(0.1)} !important`,
    border: '1px solid #dadde9',
    minWidth: '235px',
    boxShadow: `0 2px 7px 0 rgba(16, 16, 16, 0.5)`,
    padding: theme.spacing(0),
  },
}))

//#region styles

//#endregion

const styles = {
  // Regular at rest, semibold when the current page lives inside this menu —
  // the same weight rule the Sales Center links use. No outline or left pill
  // here though: those mean "this is the page", whereas selected on a
  // MenuOption only means the page is somewhere in its dropdown.
  text: navItemTextStyle,
  textSelected: {
    ...navItemTextStyle,
    fontFamily: 'Source Sans Pro Semibold',
  },
}

export interface IMenuOption {
  label: string
  url: string
  isAllowed: boolean
  dataTestId?: string
}

interface Props {
  expanded: boolean
  label: React.ReactNode
  icon?: React.ReactNode
  options: IMenuOption[]
  handleLinkClick: (url: string) => void
  location: string
  dataTestId?: string
}
export const MenuOption: FC<Props> = (props) => {
  const {
    expanded,
    label,
    icon,
    options,
    location,
    handleLinkClick,
    dataTestId,
  } = props

  const [open, setOpen] = useState(false)
  const onOpen = useCallback(() => setOpen(true), [])
  const onClose = useCallback(() => setOpen(false), [])

  const linkClick = useCallback(
    (url: string) => {
      handleLinkClick(url)
      setOpen(false)
    },
    [handleLinkClick],
  )

  const selected = useMemo(() => {
    const allUrls = options.map((o) => o.url)
    return allUrls.includes(location)
  }, [options, location])

  const allowedOptions = useMemo(
    () => options.filter((o) => o.isAllowed),
    [options],
  )

  return (
    <StyledMenuTooltip
      placement="right-start"
      arrow={expanded}
      title={
        <SubMenu
          {...props}
          options={allowedOptions}
          handleLinkClick={linkClick}
        />
      }
      enterTouchDelay={0}
      leaveTouchDelay={60000}
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      disableFocusListener
    >
      <NavItem
        selected={selected}
        disableGutters
        style={navItemTileStyle}
        data-test-id={dataTestId}
      >
        {icon ? (
          icon
        ) : (
          <StarIcon
            fontSize="inherit"
            sx={{ margin: (theme: Theme) => `0 ${theme.spacing(1.6)}` }}
          />
        )}
        <Typography sx={selected ? styles.textSelected : styles.text}>
          {label}
        </Typography>
      </NavItem>
    </StyledMenuTooltip>
  )
}
MenuOption.displayName = 'MenuOption'
